import {
  faFileAlt,
  faInfoCircle,
  faSearch,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DOMPurify from 'dompurify';
import { useDebounce } from 'helpers/utils';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, FormControl } from 'react-bootstrap';
import { MdMessage } from 'react-icons/md';
import MessageTemplateService from 'services/MessageTemplate.service';
import Flex from './Flex';

/**
 * Componente para selecionar e aplicar modelos de mensagem
 * @param {string} type - Tipo do modelo (NOTE, EMAIL, APPOINTMENT, TICKET_DESCRIPTION)
 * @param {number} ticketId - ID do ticket (opcional, apenas se o ticket já estiver salvo)
 * @param {object} editorRef - Referência para o componente Editor
 * @param {function} onTemplateApplied - Callback chamado quando um modelo é aplicado, recebe o conteúdo aplicado
 */
const MessageTemplateSelector = ({
  type,
  ticketId,
  editorRef,
  onTemplateApplied,
  position = 'down-centered'
}) => {
  const { showErrorNotification } = useNotification();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [insertMode, setInsertMode] = useState('insert'); // 'insert' ou 'replace'
  const [hoveredTemplate, setHoveredTemplate] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const dropdownToggleRef = useRef(null);
  const previousSearchTermRef = useRef('');

  // Debounce para pesquisa
  const debouncedSetSearchTerm = useDebounce(value => {
    setSearchTerm(value);
    setPageSize(10); // Reseta para 10 itens ao pesquisar
  }, 400);

  useEffect(() => {
    debouncedSetSearchTerm(searchInputValue);
  }, [searchInputValue, debouncedSetSearchTerm]);

  useEffect(() => {
    if (showDropdown && templates.length === 0) {
      loadTemplates();
    }
  }, [showDropdown]);

  // Recarrega quando pageSize ou searchTerm mudam (apenas se o dropdown estiver aberto)
  useEffect(() => {
    const wasCleared =
      previousSearchTermRef.current !== '' && searchTerm === '';
    const hasSearchOrPagination = pageSize !== 10 || searchTerm !== '';

    if (showDropdown && (hasSearchOrPagination || wasCleared)) {
      loadTemplates();
    }

    previousSearchTermRef.current = searchTerm;
  }, [pageSize, searchTerm]);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('types', type);
      params.append('size', pageSize.toString());
      params.append('page', '0');

      if (searchTerm) {
        params.append('name', searchTerm);
      }

      const response = await MessageTemplateService.list(params.toString());

      if (response.content) {
        setTemplates(response.content);
        setTotalElements(response.totalElements || 0);
      } else {
        setTemplates([]);
        setTotalElements(0);
      }
    } catch (error) {
      console.error('Erro ao carregar modelos de mensagem:', error);
      showErrorNotification('Erro ao carregar modelos de mensagem');
      setTemplates([]);
      setTotalElements(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = e => {
    setSearchInputValue(e.target.value);
  };

  const handleLoadMore = e => {
    e.stopPropagation(); // Previne fechar o dropdown
    setPageSize(prev => prev + 5);
  };

  const handleApplyTemplate = async template => {
    setIsLoading(true);
    try {
      const response = await MessageTemplateService.apply(
        template.id,
        ticketId
      );

      // Usa evaluatedResponse que contém as variáveis já substituídas pelos valores reais
      if (response.evaluatedResponse !== undefined) {
        if (editorRef?.current) {
          const quill = editorRef.current.getQuill();
          if (quill) {
            if (insertMode === 'replace') {
              // Substitui todo o conteúdo
              // Limpa o conteúdo atual primeiro
              const length = quill.getLength();
              quill.deleteText(0, length);
              // Insere o novo conteúdo sem espaços extras
              quill.clipboard.dangerouslyPasteHTML(
                0,
                response.evaluatedResponse,
                'silent'
              );
            } else {
              // Insere na posição do cursor
              const range = quill.getSelection(true);
              const index = range ? range.index : quill.getLength() - 1;
              quill.clipboard.dangerouslyPasteHTML(
                index,
                response.evaluatedResponse
              );
              // Move o cursor para o final do conteúdo inserido
              const newLength = quill.getLength();
              quill.setSelection(newLength - 1);
            }
          }
        }

        if (onTemplateApplied) {
          onTemplateApplied(response.evaluatedResponse);
        }
        setShowDropdown(false);
        setHoveredTemplate(null);
      } else {
        showErrorNotification('Erro ao aplicar modelo de mensagem');
      }
    } catch (error) {
      console.error('Erro ao aplicar modelo de mensagem:', error);
      showErrorNotification('Erro ao aplicar modelo de mensagem');
    } finally {
      setIsLoading(false);
    }
  };

  const resetDropdownState = () => {
    setHoveredTemplate(null);
    setSearchInputValue('');
    setSearchTerm('');
    setPageSize(10);
  };

  const closeDropdown = () => {
    setShowDropdown(false);
    resetDropdownState();
  };

  const handleButtonClick = e => {
    e.stopPropagation();
    setShowDropdown(prev => !prev);
    if (showDropdown) {
      resetDropdownState();
    }
  };

  // Fecha o dropdown quando clicar fora
  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = e => {
      const container = dropdownToggleRef.current;
      if (container && !container.contains(e.target)) {
        closeDropdown();
      }
    };

    // Usa setTimeout para evitar que o click que abriu o dropdown o feche imediatamente
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <div ref={dropdownToggleRef} className="position-relative d-inline-block">
      <Button
        variant="falcon-default"
        size="sm"
        disabled={isLoading}
        onClick={handleButtonClick}
        title="Modelos de mensagens"
      >
        {isLoading ? (
          <>
            <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
            <span>Carregando...</span>
          </>
        ) : (
          <>
            <MdMessage />
          </>
        )}
      </Button>

      {showDropdown && (
        <div
          className="position-absolute end-0 mt-1 border rounded shadow-lg d-flex bg-body-emphasis"
          style={{
            minWidth: '600px',
            maxWidth: 'min(800px, calc(100vw - 40px))',
            height: '500px',
            zIndex: 1050
          }}
        >
          {/* Lista de modelos - sempre ocupa 50% ou menos */}
          <div
            className="flex-shrink-0 border-end d-flex flex-column"
            style={{ width: '50%', minWidth: '300px' }}
          >
            {/* RadioGroup para escolher modo */}
            <div className="px-3 py-2 border-bottom flex-shrink-0">
              <h5 className="fw-semibold mb-2">Modelos de mensagens</h5>
              <Form.Label className="mb-2 d-block text-muted fw-medium">
                Modo de aplicação:
              </Form.Label>
              <div className="d-flex gap-3">
                <Flex alignItems="start">
                  <Form.Check
                    type="radio"
                    id="insert-mode"
                    name="templateMode"
                    checked={insertMode === 'insert'}
                    onChange={() => setInsertMode('insert')}
                    className="me-1"
                  />
                  <Form.Label className="text-muted">Inserir</Form.Label>
                </Flex>
                <Flex alignItems="start">
                  <Form.Check
                    type="radio"
                    id="replace-mode"
                    name="templateMode"
                    checked={insertMode === 'replace'}
                    onChange={() => setInsertMode('replace')}
                    className="me-1"
                  />
                  <Form.Label className="text-muted">Substituir</Form.Label>
                </Flex>
              </div>
            </div>

            {/* Campo de Pesquisa */}
            <div className="px-3 py-2 border-bottom flex-shrink-0">
              <div className="position-relative">
                <FormControl
                  type="text"
                  placeholder="Pesquisar modelos..."
                  value={searchInputValue}
                  onChange={handleSearchChange}
                  className="ps-4"
                  size="sm"
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="position-absolute text-muted"
                  style={{
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '12px'
                  }}
                />
              </div>
            </div>

            {/* Lista de templates */}
            <div className="overflow-auto flex-grow-1">
              {templates.length === 0 && !isLoading ? (
                <div className="px-3 py-2 text-muted text-center small">
                  Nenhum modelo disponível
                </div>
              ) : (
                templates.map(template => (
                  <div
                    key={template.id}
                    className="border-bottom cursor-pointer bg-transparent"
                    onMouseEnter={e => {
                      setHoveredTemplate(template);
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    onClick={() => handleApplyTemplate(template)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleApplyTemplate(template);
                      }
                    }}
                    style={{
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <div className="px-3 py-2">
                      <div className="fw-semibold text-muted">
                        {template.name}
                      </div>
                      {template.types && template.types.length > 0 && (
                        <div className="small text-body">
                          {template.types
                            .map(t => {
                              const labels = {
                                NOTE: 'Nota',
                                EMAIL: 'E-mail',
                                APPOINTMENT: 'Apontamento',
                                TICKET_DESCRIPTION: 'Descrição'
                              };
                              return labels[t] || t;
                            })
                            .join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}

              {/* Botão Ver Mais */}
              {templates.length < totalElements && (
                <div className="text-center py-2 border-top">
                  <Button
                    variant="link"
                    size="sm"
                    className="text-decoration-none text-muted py-0 px-2"
                    onClick={handleLoadMore}
                    disabled={isLoading}
                    style={{ fontSize: '0.75rem', fontWeight: 'normal' }}
                  >
                    {isLoading ? (
                      <>
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="me-1"
                        />
                        Carregando...
                      </>
                    ) : (
                      'Ver mais'
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Painel de pré-visualização - sempre visível, ocupa o resto do espaço */}
          <div
            className="flex-grow-1 d-flex flex-column"
            onMouseLeave={() => setHoveredTemplate(null)}
          >
            <div className="flex-grow-1 overflow-auto">
              {hoveredTemplate ? (
                <div className="p-3">
                  <h6 className="fw-bold mb-2">Pré-visualização</h6>
                  <div className="text-muted mb-3 fw-medium">
                    {hoveredTemplate.name}
                  </div>
                  <div
                    className="border rounded p-3 bg-1100"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(hoveredTemplate.content)
                    }}
                  />
                  <div className="mt-3">
                    <small className="text-muted">
                      Clique no modelo na lista para aplicar (
                      {insertMode === 'insert' ? 'inserir' : 'substituir'})
                    </small>
                  </div>
                </div>
              ) : (
                <div className="p-3 d-flex align-items-center justify-content-center h-100">
                  <div className="text-center text-muted">
                    <FontAwesomeIcon
                      icon={faFileAlt}
                      size="2x"
                      className="mb-2 opacity-50"
                    />
                    <div>Passe o mouse sobre um modelo para visualizar</div>
                  </div>
                </div>
              )}
            </div>

            {/* Aviso fixado no final */}
            <div className="p-3 border-top flex-shrink-0">
              <Alert variant="info" className="mb-0 py-2 fs-11">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                Digite <strong>Ctrl+M</strong> no editor para inserir modelos
                com mais rapidez.
              </Alert>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageTemplateSelector;
